import chroma from 'chroma-js';
import { nanoid } from 'nanoid';
import { subscribe } from 'valtio';
import { BRIGHT_COLORS } from '~/constants/color';
import { calculateArea } from '~/helpers/unitHelpers';
import { type DragEvent, HoverEvent } from '../CanvasEvent';
import CanvasObject from '../CanvasObject';
import { opacityWhileDragging } from '../common';
import type SurfaceBase from '../SurfaceBase';
import type { LngLat, Point } from '../SurfaceBase';
import VertexObject, { type VertexObjectProps } from '../VertexObject';

class EditPolygonLine extends CanvasObject {
  p1: VertexObject;
  p2: VertexObject;
  hoverPoint?: Point;

  width: number;
  midPoint?: Point;

  constructor(id: string, surface: SurfaceBase, p1: VertexObject, p2: VertexObject, width: number) {
    super(id, surface);
    this.p1 = p1;
    this.p2 = p2;
    this.interactive = true;
    this.sortOrder = 'a1';
    this.width = width;

    this.on('hovering', (e) => {
      this.hoverPoint = e.point;
      this.renderAll();
      this.surface.setCursor('crosshair');
    });
    this.on('hoverEnd', () => {
      this.hoverPoint = undefined;
      this.renderAll();
      this.surface.resetCursor();
    });
  }

  refreshScreenCoords(): void {
    // the vertices will take care of this
  }
  drawHitArea(ctx: OffscreenCanvasRenderingContext2D, color: string): void {
    ctx.beginPath();
    ctx.fillStyle = color;
    ctx.strokeStyle = color;
    ctx.lineWidth = this.width * 2;
    ctx.moveTo(this.p1.point.x, this.p1.point.y);
    ctx.lineTo(this.p2.point.x, this.p2.point.y);
    ctx.stroke();
  }

  getCurrentPolygonObject() {
    const currentObject = this.getStore().currentObject;
    if (currentObject?.type !== 'polygon') return;
    return currentObject;
  }

  render(ctx: CanvasRenderingContext2D): void {
    const polygon = this.getCurrentPolygonObject();
    if (!polygon) return;
    const { properties } = polygon;
    if (this.hoverPoint) {
      ctx.beginPath();
      ctx.setLineDash([]);
      ctx.arc(this.hoverPoint.x, this.hoverPoint.y, 10, 0, 2 * Math.PI);
      ctx.fillStyle = 'white';
      ctx.strokeStyle = properties.color;
      ctx.fill();
      ctx.stroke();
    }
    ctx.beginPath();
    const isDashed = properties.strokeStyle === 'dashed';
    // if the line is dashed, set the line to be dashed
    isDashed && ctx.setLineDash([10, 4]);

    if (this.midPoint) {
      ctx.setLineDash([]);
      ctx.strokeStyle = properties.color;
      ctx.moveTo(this.p1.point.x, this.p1.point.y);
      ctx.lineTo(this.midPoint.x, this.midPoint.y);
      ctx.lineTo(this.p2.point.x, this.p2.point.y);
      ctx.stroke();
    } else {
      // set the global opacity to be the strokeOpacity, this handles the vertices too.
      ctx.globalAlpha = properties.strokeOpacity;
      ctx.strokeStyle = 'properties.color';
      ctx.moveTo(this.p1.point.x, this.p1.point.y);
      ctx.lineTo(this.p2.point.x, this.p2.point.y);
      ctx.stroke();
      // This resets the line dash to ensure the point is NOT also dashed, just the line.
      ctx.setLineDash([]);
    }
  }
}

export default class EditPolygonObject extends CanvasObject {
  dragOffset: Point = { x: NaN, y: NaN };
  vertices: VertexObject[] = [];
  lines: EditPolygonLine[] = [];
  _onPolygonUpdated: (o: LngLat[]) => void;

  constructor(
    id: string,
    surface: SurfaceBase,
    onPolygonUpdated: (p: LngLat[]) => void,
    // interactiveOptions: InteractiveOptions = { interactiveVertices: true },
  ) {
    super(id, surface);
    const store = this.getStore();
    const polygon = this.getCurrentPolygonObject();
    if (!polygon) throw new Error('currentObject is not a polygon!');
    this.cleanupFns.push(
      subscribe(store, () => {
        this.surface.renderAll();
      }),
    );
    this._onPolygonUpdated = onPolygonUpdated;
    this.interactive = true;

    // controls whether object has interactive vertices or not,
    // by default, Polygons will have interactivity.
    const interactive = polygon.interactive ? polygon.interactive.vertices !== false : true;

    // exclude last coordinate since its just the first
    this.vertices = polygon.geometry.coordinates[0].slice(0, -1).map((c) => {
      const lngLat = {
        lng: c[0],
        lat: c[1],
      };

      return this.createVertex(
        {
          lngLat,
          point: this.surface.toScreenCoords(lngLat),
        },
        interactive,
      );
    });
    this.createLines();

    this.on('dragStart', () => {
      // hide all the vertices and the lines when dragging
      this.vertices.forEach((v) => {
        v.visible = false;
      });
      this.dragOffset.x = 0;
      this.dragOffset.y = 0;
      this.removeLines();
      this.resetDragOffset();
      this.renderAll();
    });
    this.on('dragging', (e) => {
      this.dragOffset = e.diff();
      this.renderAll();
    });
    this.on('dragEnd', () => {
      this.vertices.forEach((v) => {
        v.point.x += this.dragOffset.x;
        v.point.y += this.dragOffset.y;
        v.refreshMapCoords();
        v.visible = true;
      });
      this.createLines();
      this.resetDragOffset();
      this.onPolygonUpdated();
      this.renderAll();
    });
  }

  createVertex(_v: VertexObjectProps, interactive: boolean = true) {
    const v = new VertexObject(nanoid(), this.surface, _v);
    v.interactive = interactive;

    if (interactive === false) {
      v.hidden = true;
    }

    v.on('dragging', (e) => this.updateVertex(v, e));
    v.on('dragEnd', () => this.onPolygonUpdated());

    v.on('tap', () => {
      if (this.vertices.length <= 3) {
        return;
      }

      const index = this.vertices.findIndex(({ id }) => id === v.id);

      this.surface.disableInteractionEvents();
      const popup = window.document.createElement('div');
      popup.className = 'min-w-[8em] bg-white cursor-pointer border';
      popup.style.position = 'absolute';
      popup.style.top = `${v.point.y + 10}px`;
      popup.style.left = `${v.point.x + 10}px`;

      const deleteItem = window.document.createElement('div');
      deleteItem.className = 'px-4 py-3 border-b hover:bg-brand-light';
      deleteItem.innerText = 'Delete';
      deleteItem.onclick = () => {
        this.vertices.splice(index, 1);
        v.destroy();
        this.removeLines();
        this.createLines();
        this.surface.enableInteractionEvents();
        this.renderAll();
        this.onPolygonUpdated();
        dismiss();
      };

      popup.appendChild(deleteItem);

      this.surface.canvas.parentElement!.appendChild(popup);

      const dismiss = () => {
        this.surface.enableInteractionEvents();
        popup.remove();
        v.emit(
          'hoverEnd',
          new HoverEvent('hoverEnd', {
            point: { x: 0, y: 0 },
            lngLat: { lng: 0, lat: 0 },
          }),
        );
        this.surface.canvas.removeEventListener('mouseup', dismiss);
      };
      this.surface.canvas.addEventListener('mouseup', dismiss);
    });

    this.addChild(v);
    return v;
  }

  onPolygonUpdated() {
    this._onPolygonUpdated([...this.vertices, this.vertices[0]].map((v) => v.lngLat));
  }

  resetDragOffset = () => {
    this.dragOffset = { x: NaN, y: NaN };
  };

  updateVertex = (v: VertexObject, e: DragEvent) => {
    this.removeLines();
    v.point = e.currentPoint;
    v.lngLat = e.currentLngLat;
    this.createLines();
    this.renderAll();
  };

  getCurrentPolygonObject() {
    const currentObject = this.getStore().currentObject;
    if (currentObject?.type !== 'polygon') return;
    return currentObject;
  }

  createLines = () => {
    const polygon = this.getCurrentPolygonObject();
    if (!polygon) return;
    const { properties } = polygon;
    const firstVertex = this.vertices[0];
    const allVertices = [...this.vertices, firstVertex];

    // check if lines are NOT interactive
    const interactive = polygon.interactive?.lines !== false;

    // draw the lines
    for (let i = 0; i < allVertices.length - 1; i++) {
      const line = new EditPolygonLine(
        `l-${i}-${this.id}`,
        this.surface,
        allVertices[i],
        allVertices[i + 1],
        properties.strokeWidth,
      );
      // mid point creation has started
      line.on('dragStart', (e) => {
        const v = this.createVertex(e);
        this.vertices.splice(i + 1, 0, v);
        this.addChild(v);
        v.on('dragging', (e) => this.updateVertex(v, e));
        v.on('dragEnd', () => this.onPolygonUpdated());
      });
      // mid point is being moved
      line.on('dragging', (e) => {
        this.removeLines();
        const v = this.vertices[i + 1];
        v.point = e.currentPoint;
        v.lngLat = e.currentLngLat;
        this.createLines();
        this.renderAll();
      });
      // mid point creation has ended
      line.on('dragEnd', () => this.onPolygonUpdated());

      // set whether line is interactive or not.
      line.interactive = interactive;

      this.addChild(line);
      this.lines.push(line);
    }
  };

  removeLines = () => {
    this.lines.forEach((line) => {
      this.removeChild(line);
    });
    this.lines = [];
  };

  isDragging = () => {
    return !Number.isNaN(this.dragOffset.x);
  };

  // inspired by https://github.com/HarryStevens/geometric/blob/master/src/polygons/polygonCentroid.js
  centroid = (): Point => {
    const vertices = [...this.vertices];
    let a = 0,
      x = 0,
      y = 0,
      l = vertices.length;

    for (let i = 0; i < l; i++) {
      const s = i === l - 1 ? 0 : i + 1,
        v0 = vertices[i],
        v1 = vertices[s],
        f = v0.point.x * v1.point.y - v1.point.x * v0.point.y;

      a += f;
      x += (v0.point.x + v1.point.x) * f;
      y += (v0.point.y + v1.point.y) * f;
    }

    const d = a * 3;

    return { x: x / d, y: y / d };
  };

  getPolygonBounds() {
    let minX = this.vertices[0].point.x;
    let maxX = this.vertices[0].point.x;
    let minY = this.vertices[0].point.y;
    let maxY = this.vertices[0].point.y;

    for (let i = 0; i < this.vertices.length; i++) {
      minX = Math.min(minX, this.vertices[i].point.x);
      maxX = Math.max(maxX, this.vertices[i].point.x);
      minY = Math.min(minY, this.vertices[i].point.y);
      maxY = Math.max(maxY, this.vertices[i].point.y);
    }

    const width = maxX - minX;
    const height = maxY - minY;

    return { width, height };
  }

  drawBoundingBoxHitArea(ctx: OffscreenCanvasRenderingContext2D, color: string) {
    let smallestX = this.vertices[0].point.x;
    let biggestX = this.vertices[0].point.x;
    let smallestY = this.vertices[1].point.y;
    let biggestY = this.vertices[1].point.y;

    for (let i = 0; i < this.vertices.length; i++) {
      if (this.vertices[i].point.x <= smallestX) smallestX = this.vertices[i].point.x;
      if (this.vertices[i].point.x >= biggestX) biggestX = this.vertices[i].point.x;
      if (this.vertices[i].point.y <= smallestY) smallestY = this.vertices[i].point.y;
      if (this.vertices[i].point.y >= biggestY) biggestY = this.vertices[i].point.y;
    }
    const boundingBox = {
      startX: smallestX,
      startY: smallestY,
      distX: biggestX - smallestX,
      distY: biggestY - smallestY,
    };

    ctx.beginPath();
    ctx.fillStyle = color;
    const padding = 20;
    ctx.fillRect(
      boundingBox.startX - padding,
      boundingBox.startY - padding,
      boundingBox.distX + 2 * padding,
      boundingBox.distY + 2 * padding,
    );
    ctx.stroke();
  }

  render(ctx: CanvasRenderingContext2D) {
    const polygon = this.getCurrentPolygonObject();
    if (!polygon) return;
    const store = this.getStore();
    const { properties } = polygon;
    const isDashed = properties.strokeStyle === 'dashed';
    // set global transparency to be 1 (fully opaque)
    // line opacity is handled in the 'LineObject' up top.
    ctx.globalAlpha = 1;
    // the object is currently being moved
    if (this.isDragging()) {
      // render just the polygon
      if (this.vertices.length >= 2) {
        // if dragging & fillOpacity is greater than 0.7, set opacity to be 30% transparent.

        if (properties.fillOpacity > opacityWhileDragging) {
          ctx.globalAlpha = opacityWhileDragging;
        }

        ctx.fillStyle = chroma(properties.color).alpha(properties.fillOpacity).hex();
        ctx.beginPath();
        isDashed && ctx.setLineDash([10, 4]);
        ctx.strokeStyle = properties.color;
        const firstPoint = this.vertices[0];
        ctx.moveTo(this.dragOffset.x + firstPoint.point.x, this.dragOffset.y + firstPoint.point.y);

        for (let i = 1; i < this.vertices.length; i++) {
          const p = this.vertices[i];
          ctx.lineTo(this.dragOffset.x + p.point.x, this.dragOffset.y + p.point.y);
        }
        ctx.closePath();
        ctx.stroke();
        ctx.fill();
      }
      return;
    }

    // Note: vertices are rendered by the object
    // lines
    if (this.vertices.length > 0) {
      // polygon
      if (this.vertices.length >= 2) {
        ctx.fillStyle = chroma(properties.color).alpha(properties.fillOpacity).hex();
        isDashed && ctx.setLineDash([10, 4]);
        ctx.beginPath();
        const firstPoint = this.vertices[0];
        ctx.moveTo(firstPoint.point.x, firstPoint.point.y);

        for (let i = 1; i < this.vertices.length; i++) {
          const p = this.vertices[i];
          ctx.lineTo(p.point.x, p.point.y);
        }

        ctx.setLineDash([]);
        ctx.closePath();
        ctx.fill();
      }

      if (this.vertices.length >= 2) {
        let smallestX = this.vertices[0].point.x;
        let biggestX = this.vertices[0].point.x;
        let smallestY = this.vertices[1].point.y;
        let biggestY = this.vertices[1].point.y;

        for (let i = 0; i < this.vertices.length; i++) {
          if (this.vertices[i].point.x <= smallestX) smallestX = this.vertices[i].point.x;
          if (this.vertices[i].point.x >= biggestX) biggestX = this.vertices[i].point.x;
          if (this.vertices[i].point.y <= smallestY) smallestY = this.vertices[i].point.y;
          if (this.vertices[i].point.y >= biggestY) biggestY = this.vertices[i].point.y;
        }

        const boundingBox = {
          startX: smallestX,
          startY: smallestY,
          distX: biggestX - smallestX,
          distY: biggestY - smallestY,
        };

        ctx.beginPath();
        ctx.strokeStyle = properties.color;
        ctx.lineWidth = 1;
        ctx.fillStyle = properties.color;
        const padding = 20;
        ctx.rect(
          boundingBox.startX - padding,
          boundingBox.startY - padding,
          boundingBox.distX + 2 * padding,
          boundingBox.distY + 2 * padding,
        );

        // padding for the bounding box
        const rectCornerBoxWidth = 5;

        // top left corner
        ctx.fillRect(
          boundingBox.startX - padding - 0.5 * rectCornerBoxWidth,
          boundingBox.startY - padding - 0.5 * rectCornerBoxWidth,
          rectCornerBoxWidth,
          rectCornerBoxWidth,
        );

        // top right corner
        ctx.fillRect(
          boundingBox.startX + boundingBox.distX + padding - 0.5 * rectCornerBoxWidth,
          boundingBox.startY - padding - 0.5 * rectCornerBoxWidth,
          rectCornerBoxWidth,
          rectCornerBoxWidth,
        );

        // bottom right corner
        ctx.fillRect(
          boundingBox.startX + boundingBox.distX + padding - 0.5 * rectCornerBoxWidth,
          boundingBox.startY + boundingBox.distY + padding - 0.5 * rectCornerBoxWidth,
          rectCornerBoxWidth,
          rectCornerBoxWidth,
        );

        // bottom left corner
        ctx.fillRect(
          boundingBox.startX - padding - 0.5 * rectCornerBoxWidth,
          boundingBox.startY + boundingBox.distY + padding - 0.5 * rectCornerBoxWidth,
          rectCornerBoxWidth,
          rectCornerBoxWidth,
        );

        ctx.stroke();
      }

      if (this.vertices.length >= 2) {
        const a = calculateArea({
          unitSystem: store.currentUnitSystem,
          geometry: {
            type: 'Polygon',
            coordinates: [[...this.vertices.map((c) => c.getLngLatAsArray()), this.vertices[0].getLngLatAsArray()]],
          },
        });
        const centroid = this.centroid();

        const padding = 10;
        const fontSize = 14;
        ctx.font = `bold ${fontSize}px arial`; // NOTE: font size must be set before measureText to avoid dynamic scaling when object visibility changes
        const areaText = `${a.value.toLocaleString()} ${a.unit}`;
        const areaTextWidth = ctx.measureText(areaText).width;
        const { title } = polygon;
        const titleTextWidth = ctx.measureText(title).width;
        const polygonBounds = this.getPolygonBounds();
        ctx.lineWidth = properties.strokeWidth;

        let labelText: string;
        let textWidth: number;

        if (properties.showArea && properties.showTitle) {
          if (title === '') {
            labelText = areaText;
            textWidth = areaTextWidth;
          } else {
            labelText = `${title}\n${areaText}`;
            textWidth = Math.max(areaTextWidth, titleTextWidth);
          }
        } else if (properties.showArea && !properties.showTitle) {
          labelText = areaText;
          textWidth = areaTextWidth;
        } else if (properties.showTitle && !properties.showArea) {
          labelText = title;
          textWidth = titleTextWidth;
        } else {
          labelText = '';
          textWidth = 0;
        }

        if (properties.labelAlwaysVisible || textWidth <= polygonBounds.width) {
          if (labelText !== '' && textWidth !== 0) {
            const lines = labelText.trim() === '' ? ['Empty Text'] : labelText.split('\n');
            const textHeight = fontSize * lines.length;
            ctx.fillStyle = properties.color;
            ctx.beginPath();
            ctx.roundRect(
              centroid.x - 0.5 * textWidth - padding / 2,
              centroid.y - 0.5 * textHeight - padding / 2,
              textWidth + padding,
              // note the additional padding on the height for each additional line of text
              textHeight + padding + (lines.length - 1) * padding,
              [5],
            );
            ctx.fill();
            ctx.textBaseline = 'top';
            ctx.textAlign = 'center';
            ctx.fillStyle = BRIGHT_COLORS.includes(properties.color) ? '#000000' : '#FFFFFF';
            const x = centroid.x;
            const y = centroid.y - 0.5 * textHeight;
            for (let i = 0; i < lines.length; i++) {
              ctx.fillText(lines[i], x, y + i * fontSize + (i !== 0 ? padding : 0));
            }
          }
        }
      }
    }
  }

  drawHitArea(ctx: OffscreenCanvasRenderingContext2D, color: string): void {
    if (this.isDragging()) return;
    ctx.save();
    ctx.fillStyle = color;

    if (this.vertices.length) {
      const p1 = this.vertices[0].point;
      ctx.beginPath();
      ctx.moveTo(p1.x, p1.y);

      for (let i = 1; i < this.vertices.length; i++) {
        const { x, y } = this.vertices[i].point;
        ctx.lineTo(x, y);
      }

      ctx.closePath();
      ctx.fill();
    }

    ctx.restore();
    return;
  }
}
